import {
  formatDisplayDateAtPrecision,
  toDisplayDate,
  type Calendar,
  type CanonicalDay,
} from '@dl/calendar';
import { type Precision } from '@dl/shared';
import { button, constant, div, span, text, type Mountable } from '@llui/dom';

import {
  calendarDatePicker,
  seedCursor,
  type CalendarCursor,
  type CalendarDatePickerMessages,
  type CalendarDatePickerOverlay,
  type CalendarGranularity,
  type SubDayPeriod,
} from './calendar-date-picker.js';
import { chevronDownIcon } from './icon.js';
import { popover } from './popover.js';

/**
 * `<DateField>` — the compact, calendar-aware date control wired everywhere a
 * single in-world day is picked (the session/browse cursors, `game_date`
 * facts, validity bounds). It is the *field* shell around the full
 * `calendarDatePicker` grid: a trigger button showing the formatted value
 * that opens the grid in an anchored `popover`.
 *
 * It supersedes the older four-`<select>` `calendarInput`: same `value`
 * (a canonical-day integer) / `onChange`-shaped contract at the call site,
 * but the dropdown is the rich grid (month view, keyboard nav, today ring,
 * intercalary bands, "Now"/"Clear").
 *
 * ## Stateless atom (host owns the open state)
 *
 * Like `calendarDatePicker`, this is a pure function of its props. Only ONE
 * grid is open app-wide, so the host keeps a single `{ id, cursor, overlay }`
 * slice and passes `open` / `cursor` / `overlay` here, re-rendering (via a
 * keyed `each` digesting value+open+cursor+overlay) on change. The field
 * emits intent through callbacks:
 *
 *  - `onOpen(seed)` — trigger clicked; `seed` is the view cursor derived from
 *    the current value (call `seedCursor` is done for you).
 *  - `onNavigate` / `onOverlay` — forwarded straight from the grid.
 *  - `onSelect(day)` — a day was committed; the field then `onClose`s itself
 *    (picking a date closes the popover, the standard date-field gesture).
 *  - `onClear` — the grid's Clear was used (then `onClose`).
 *  - `onClose` — scrim / ESC dismissal.
 *
 * For the always-open, no-trigger case (a modal whose whole body IS the
 * picker) render `calendarDatePicker` directly instead — this shell only
 * earns its keep when the grid should collapse to a value.
 */

export interface DateFieldMessages {
  /** Trigger text when no date is set. */
  placeholder: string;
  /** Accessible name for the trigger button. */
  trigger: string;
  /** Accessible name for the popover dismiss scrim. */
  close: string;
}

export const DEFAULT_DATE_FIELD_MESSAGES: DateFieldMessages = {
  placeholder: 'Set date…',
  trigger: 'Choose a date',
  close: 'Close date picker',
};

export interface DateFieldOptions {
  /** Active calendar (from `@dl/calendar`). */
  calendar: Calendar;
  /** Committed canonical day, or `null` for no selection. */
  value: CanonicalDay | number | null;
  /** Whether THIS field's grid is currently open (host-owned single-open slice). */
  open: boolean;
  /** View cursor while open (host-owned); derived from `value` when omitted. */
  cursor?: CalendarCursor;
  /** Inline overlay (month list / era) while open (host-owned). */
  overlay?: CalendarDatePickerOverlay;
  /** Stable id — a11y wiring + the host's open-slice key. */
  id: string;
  /**
   * Which edge the popover grid anchors to. `start` (default) aligns the
   * panel's leading edge with the trigger; `end` aligns the trailing edge —
   * use it for a trigger near the viewport's inline-end (e.g. the top-bar
   * browse pill) so the 336px grid opens inward instead of overflowing.
   */
  align?: 'start' | 'end';
  /**
   * Disable the field — the trigger renders inert (no popover opens). Used
   * while a value is provisional (e.g. the campaign cursor before the op-log
   * fold confirms the active calendar).
   */
  disabled?: boolean;
  /**
   * Optional content rendered inside the popover, ABOVE the grid — e.g. a
   * calendar-type switcher. Decouples `@dl/ui` from the host's vocabulary:
   * the host builds the switcher and passes it in. Only shown while open.
   */
  header?: Mountable;
  /** Show the grid's Clear affordance (open-ended bounds). Default false. */
  clearable?: boolean;
  /** Session "now" canonical day — ringed in the grid. */
  today?: CanonicalDay | number | null;
  /** Show the grid's footer "Now" jump. Default true; false for the cursor. */
  showNow?: boolean;
  /**
   * Selection granularity forwarded to the grid (ADR-0005 §5). Coarser than
   * `day` makes the popover pick at month/year level (a fuzzy validity bound
   * picks at its own precision). Default `day`.
   */
  granularity?: CalendarGranularity;
  /**
   * Selectable granularity levels. When more than one is given, the grid shows
   * an in-place segmented control and emits `onGranularity` — the field becomes
   * a self-contained fuzzy-date picker. Forwarded straight to the grid.
   */
  granularities?: readonly CalendarGranularity[];
  /** The user switched granularity in the grid's segmented control. */
  onGranularity?: (granularity: CalendarGranularity) => void;
  /** Label for an optional leading "none" segment in the granularity control
   * that clears the value (forwarded to the grid). */
  granularityClearLabel?: string;
  /** The user picked the clear ("none") segment. */
  onGranularityClear?: () => void;
  /** Show the grid footer's "canonical day N" debug sub-line. Default false. */
  showCanonicalDay?: boolean;
  /** Sub-day periods to expose as the grid's time row. */
  subDayPeriods?: readonly SubDayPeriod[];
  /** Open the grid, seeded at `seed` (the cursor derived from `value`). */
  onOpen: (seed: CalendarCursor) => void;
  /** Dismiss the grid (scrim / ESC / after a pick). */
  onClose: () => void;
  /** The user navigated the open grid. */
  onNavigate: (cursor: CalendarCursor) => void;
  /** The user toggled the inline month/era overlay. */
  onOverlay: (overlay: CalendarDatePickerOverlay) => void;
  /** The user committed a day. */
  onSelect: (day: CanonicalDay) => void;
  /** The user cleared the selection (only meaningful when `clearable`). */
  onClear?: () => void;
  /** Display locale. Defaults to `en`. */
  locale?: string;
  /** Message overrides (field chrome + the grid's own catalog). */
  messages?: Partial<DateFieldMessages & CalendarDatePickerMessages>;
  /** `data-testid` prefix. */
  testid?: string;
}

/**
 * The data-model precision a UI granularity reads as. `day` is the exact,
 * no-hint level (renders the full date); the coarser levels map 1:1 to their
 * precision so the closed chip reads honestly fuzzy ("1482" / "Hammer 1482").
 */
function precisionForGranularity(g: CalendarGranularity | undefined): Precision | undefined {
  return g === undefined || g === 'day' ? undefined : g;
}

/**
 * Format a canonical day as the calendar's display string at the field's
 * granularity, or `null`. A coarse granularity renders fuzzily (the stored day
 * stays exact — ADR-0005 §5).
 */
function formatValue(
  calendar: Calendar,
  value: CanonicalDay | number | null,
  locale: string,
  granularity: CalendarGranularity | undefined,
): string | null {
  if (value === null) return null;
  const r = toDisplayDate(calendar, value);
  return r.ok
    ? formatDisplayDateAtPrecision(calendar, r.value, precisionForGranularity(granularity), { locale })
    : null;
}

export function dateField(options: DateFieldOptions): Mountable {
  const { calendar, value, open, id, locale = 'en', testid } = options;
  const messages: DateFieldMessages = { ...DEFAULT_DATE_FIELD_MESSAGES, ...options.messages };
  const tid = (suffix: string): Record<string, string> =>
    testid !== undefined ? { 'data-testid': `${testid}-${suffix}` } : {};

  const formatted = formatValue(calendar, value, locale, options.granularity);
  const panelId = `${id}-grid`;
  const triggerId = `${id}-trigger`;

  const disabled = options.disabled === true;
  const trigger = button(
    {
      type: 'button',
      id: triggerId,
      class: 'dl-date-field-trigger',
      'data-empty': formatted === null ? 'true' : 'false',
      'aria-haspopup': 'dialog',
      'aria-expanded': open ? 'true' : 'false',
      'aria-controls': panelId,
      'aria-label': messages.trigger,
      ...(disabled ? { disabled: true, 'aria-disabled': 'true' } : {}),
      ...tid('trigger'),
      // Open on POINTER-DOWN, not click: when another control (e.g. a fact's
      // text input) holds focus, a real mouse click first fires `focusout`
      // there, which can re-render this row and replace the trigger node
      // *between* mousedown and mouseup — so the trailing `click` lands on a
      // detached node and is lost. Opening on pointerdown commits the open
      // within the same gesture, before any blur-driven remount. The popover
      // scrim's own mousedown-dismiss (#142) means a press while open closes,
      // so we only OPEN here when currently closed.
      onPointerDown: (event: PointerEvent) => {
        if (disabled || open) return;
        // Primary button / touch / pen only (ignore right/middle click).
        if (event.button !== 0) return;
        event.preventDefault();
        options.onOpen(seedCursor(calendar, value));
      },
      // Keyboard activation (Enter/Space on the focused button) fires `click`
      // but no `pointerdown` — keep the click path so the picker stays
      // keyboard-openable. It no-ops for mouse (already open from pointerdown).
      onClick: () => {
        if (disabled || open) return;
        options.onOpen(seedCursor(calendar, value));
      },
    },
    [
      span({ class: 'dl-date-field-value' }, [
        text(formatted ?? messages.placeholder),
      ]),
      span({ class: 'dl-date-field-caret', 'aria-hidden': 'true' }, [chevronDownIcon({ size: 14 })]),
    ],
  );

  const children: Mountable[] = [trigger];

  if (open) {
    children.push(
      popover({
        open: constant(open),
        onDismiss: () => options.onClose(),
        triggerId,
        panelClass: 'dl-date-field-popover',
        panelId,
        labels: { close: messages.close },
        // Collision-aware anchored positioning (flip + shift) via the shared
        // floating primitive: the grid stays in view when the trigger is near a
        // viewport margin instead of overflowing. `align` chooses the preferred
        // edge; floating-ui flips it to the opposite side when there isn't room.
        floating: {
          placement: options.align === 'end' ? 'bottom-end' : 'bottom-start',
          offset: 4,
        },
        ...(testid !== undefined ? { testid: `${testid}-popover` } : {}),
        children: [
          calendarDatePicker({
            calendar,
            value,
            cursor: options.cursor ?? seedCursor(calendar, value),
            overlay: options.overlay ?? 'none',
            mode: 'inline',
            // The switcher rides INSIDE the grid card as its first row, so it
            // reads as one control rather than a detached strip above it.
            ...(options.header !== undefined ? { header: options.header } : {}),
            ...(options.today !== undefined ? { today: options.today } : {}),
            ...(options.showNow !== undefined ? { showNow: options.showNow } : {}),
            ...(options.granularity !== undefined ? { granularity: options.granularity } : {}),
            ...(options.granularities !== undefined ? { granularities: options.granularities } : {}),
            ...(options.onGranularity !== undefined
              ? { onGranularity: options.onGranularity }
              : {}),
            ...(options.granularityClearLabel !== undefined
              ? { granularityClearLabel: options.granularityClearLabel }
              : {}),
            ...(options.onGranularityClear !== undefined
              ? {
                  // Picking "None" (open-ending the bound) is a terminal choice
                  // like committing a day → close the popover. Without this the
                  // year view (which deliberately stays open on +/− steps) had
                  // no in-panel close path after clearing.
                  onGranularityClear: () => {
                    options.onGranularityClear?.();
                    options.onClose();
                  },
                }
              : {}),
            ...(options.showCanonicalDay !== undefined
              ? { showCanonicalDay: options.showCanonicalDay }
              : {}),
            ...(options.subDayPeriods !== undefined
              ? { subDayPeriods: options.subDayPeriods }
              : {}),
            locale,
            ...(testid !== undefined ? { testid: `${testid}-cal` } : {}),
            ...(options.messages !== undefined ? { messages: options.messages } : {}),
            onNavigate: (cursor) => options.onNavigate(cursor),
            onOverlay: (overlay) => options.onOverlay(overlay),
            onSelect: (day) => {
              options.onSelect(day);
              // Day / month grids commit on a single click → close. The year
              // stepper commits on every +/− step, so it stays open and closes
              // on dismiss instead (otherwise one step would close it).
              if (options.granularity !== 'year') options.onClose();
            },
            ...(options.clearable === true
              ? {
                  onClear: () => {
                    options.onClear?.();
                    options.onClose();
                  },
                }
              : {}),
          }),
        ],
      }),
    );
  }

  return div(
    {
      class: 'dl-date-field',
      ...(testid !== undefined ? { 'data-testid': testid } : {}),
    },
    children,
  );
}
